import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertCircle, AlertTriangle, ArrowRight, RefreshCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { QueryStateBoundary } from '@/components/ui/query-state-boundary';

import { FirstRunCard } from '@/components/FirstRunCard';
import { POLLING_INTERVALS } from '@/constants/polling';
import { useAuth } from '@/contexts/AuthContext';
import { usePmProperty } from '@/hooks/usePmProperty';
import { useVisibilityPolling } from '@/hooks/useVisibilityPolling';
import { isCommercial } from '@/lib/property-type';
import { formatStatusLabel } from '@/lib/utils';
import {
  getPropertyStats,
  getPropertyConfig,
  getPmAccessLogLive,
  getPmIncidents,
  getPmPendingWalkIns,
  getAllDevices,
  getPmGates,
  getPmUpcomingVisitors,
  getPropertyModules,
  flagVisit,
  clearVisitFlag,
} from '@/services/api.client';
import { StatCard } from '@/components/pm/StatCard';
import { LiveFeed } from '@/components/pm/LiveFeed';
import { EventDetailPanel } from '@/components/pm/EventDetailPanel';
import { Badge } from '@/components/ui/badge';
import type {
  PmAccessLogEntry,
  PropertyGate,
  GuardDevice,
  Invitation,
  PropertyModule,
} from '@/types';

export function DashboardPage() {
  const pm = usePmProperty();
  const { user } = useAuth();
  const propertyId = pm?.propertyId ?? '';
  // Security supervisors share this dashboard but the server restricts devices,
  // modules, and the invitation list to property managers (security-model.md).
  // Skip those requests for supervisors so they don't trigger 403 errors.
  const isManager = pm?.role === 'property_manager';
  const [selected, setSelected] = useState<PmAccessLogEntry | null>(null);
  const queryClient = useQueryClient();

  const flagMutation = useMutation({
    mutationFn: ({ visitId, reason }: { visitId: string; reason: string }) =>
      flagVisit(propertyId, visitId, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pm', 'access-logs', 'live', propertyId] });
      setSelected(null);
    },
  });

  const clearFlagMutation = useMutation({
    mutationFn: (visitId: string) => clearVisitFlag(propertyId, visitId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pm', 'access-logs', 'live', propertyId] });
      setSelected(null);
    },
  });

  const handleFlag = (entry: PmAccessLogEntry) => {
    const reason = window.prompt('Reason for flagging this visitor (management note):');
    if (reason && reason.trim().length >= 3) {
      flagMutation.mutate({ visitId: entry.id, reason: reason.trim() });
    }
  };

  const fastInterval = useVisibilityPolling(POLLING_INTERVALS.PM.FAST);
  const mediumInterval = useVisibilityPolling(POLLING_INTERVALS.PM.MEDIUM);
  const slowInterval = useVisibilityPolling(POLLING_INTERVALS.PM.SLOW);

  const statsQuery = useQuery({
    queryKey: ['pm', 'stats', propertyId],
    queryFn: () => getPropertyStats(propertyId),
    enabled: !!propertyId,
    refetchInterval: mediumInterval,
  });

  // Drives the residential vs commercial copy on the stat tiles. The
  // underlying numbers are identical — a count of access events is a count
  // of access events whether the building hosts residents or employees —
  // only the user-facing label changes. ADR 009 slice 3.
  const propertyConfigQuery = useQuery({
    queryKey: ['pm', 'property-config', propertyId],
    queryFn: () => getPropertyConfig(propertyId),
    enabled: !!propertyId,
  });
  const isBuildingManager = isCommercial(propertyConfigQuery.data?.propertyType);

  const liveQuery = useQuery({
    queryKey: ['pm', 'access-logs', 'live', propertyId],
    queryFn: () => getPmAccessLogLive(propertyId),
    enabled: !!propertyId,
    refetchInterval: fastInterval,
  });

  const incidentsQuery = useQuery({
    queryKey: ['pm', 'incidents', propertyId, 'open'],
    queryFn: () => getPmIncidents(propertyId, { status: 'open', limit: 5 }),
    enabled: !!propertyId,
    refetchInterval: mediumInterval,
  });

  const walkInsQuery = useQuery({
    queryKey: ['pm', 'walk-ins', 'pending', propertyId],
    queryFn: () => getPmPendingWalkIns(propertyId),
    enabled: !!propertyId,
    refetchInterval: fastInterval,
  });

  const gatesQuery = useQuery({
    queryKey: ['pm', 'gates', propertyId],
    queryFn: () => getPmGates(propertyId),
    enabled: !!propertyId,
    refetchInterval: slowInterval,
  });

  const devicesQuery = useQuery({
    queryKey: ['pm', 'devices', propertyId],
    queryFn: () => getAllDevices(propertyId),
    enabled: !!propertyId && isManager,
    refetchInterval: slowInterval,
  });

  const upcomingQuery = useQuery({
    queryKey: ['pm', 'upcoming', propertyId],
    queryFn: () => getPmUpcomingVisitors(propertyId),
    enabled: !!propertyId && isManager,
    refetchInterval: mediumInterval,
  });

  const modulesQuery = useQuery({
    queryKey: ['pm', 'modules', propertyId],
    queryFn: () => getPropertyModules(propertyId),
    enabled: !!propertyId && isManager,
    refetchInterval: slowInterval,
  });

  const stats = statsQuery.data;
  const liveEntries = liveQuery.data ?? [];

  const anyError = [
    statsQuery,
    liveQuery,
    incidentsQuery,
    walkInsQuery,
    gatesQuery,
    ...(isManager ? [devicesQuery, upcomingQuery, modulesQuery] : []),
  ].some((q) => q.isError);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl">Dashboard</h1>

      {user && (
        <FirstRunCard
          storageKey={`sl.firstrun.manager:${user.id}`}
          title="Welcome — let's get your property set up"
          description="Start with these key steps to get your estate running."
          actions={[
            { label: 'Property setup', to: '/manager/property' },
            { label: 'Invite guards', to: '/manager/guards' },
            { label: 'Modules', to: '/manager/modules' },
          ]}
        />
      )}

      {anyError && (
        <div
          role="alert"
          className="flex items-start justify-between gap-3 rounded-xl border border-[var(--border-error)] bg-[var(--status-red-tint)] p-4"
        >
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 size-4 shrink-0 text-[var(--status-red-text)]" />
            <div className="space-y-0.5">
              <p className="text-sm font-medium text-[var(--text-primary)]">
                Some dashboard data couldn&apos;t be loaded
              </p>
              <p className="text-sm text-[var(--text-secondary)]">
                Sections below may show stale or missing information.
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 shrink-0"
            onClick={() => {
              void statsQuery.refetch();
              void liveQuery.refetch();
              void incidentsQuery.refetch();
              void walkInsQuery.refetch();
              void gatesQuery.refetch();
              if (isManager) {
                void devicesQuery.refetch();
                void upcomingQuery.refetch();
                void modulesQuery.refetch();
              }
            }}
          >
            <RefreshCcw className="size-3.5" />
            Retry all
          </Button>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label={isBuildingManager ? 'Visitors Today' : 'Events Today'}
          value={stats?.eventsToday ?? 0}
          {...(stats?.eventsTodayDelta !== undefined ? { delta: stats.eventsTodayDelta } : {})}
          loading={statsQuery.isLoading}
        />
        <StatCard
          label={isBuildingManager ? 'On Premises Now' : 'On Property Now'}
          value={stats?.onPropertyNow ?? 0}
          loading={statsQuery.isLoading}
        />
        <StatCard
          label="Open Incidents"
          value={stats?.openIncidents ?? 0}
          alert={(stats?.openIncidents ?? 0) > 0}
          loading={statsQuery.isLoading}
        />
        <StatCard
          label="Guards Online"
          value={stats?.guardsOnline ?? 0}
          {...(stats ? { suffix: `/ ${stats.guardsTotal}` } : {})}
          loading={statsQuery.isLoading}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Live feed — spans 2 cols */}
        <div className="lg:col-span-2 rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface-raised)] p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold">Live Access Feed</h2>
            <Link
              to="/manager/access-log"
              className="text-xs text-[var(--text-tertiary)] flex items-center gap-1 hover:text-[var(--text-primary)]"
            >
              View all <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <LiveFeed entries={liveEntries} loading={liveQuery.isLoading} onSelect={setSelected} />
        </div>

        {/* Right column: incidents + walk-ins */}
        <div className="space-y-4">
          {/* Open Incidents card */}
          <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface-raised)] p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold flex items-center gap-2">
                {(stats?.openIncidents ?? 0) > 0 && (
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                )}
                Open Incidents
              </h2>
              <Link
                to="/manager/incidents"
                className="text-xs text-[var(--text-tertiary)] flex items-center gap-1 hover:text-[var(--text-primary)]"
              >
                View all <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            <QueryStateBoundary
              query={incidentsQuery}
              isEmpty={(d) => (d?.data ?? []).length === 0}
              errorTitle="Couldn't load incidents"
              emptyMessage="No open incidents"
              loadingFallback={<p className="text-sm text-[var(--text-tertiary)]">Loading…</p>}
            >
              {(d) => (
                <ul className="space-y-2">
                  {d.data.map((inc) => (
                    <li key={inc.id} className="text-sm">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium">{inc.referenceNumber}</p>
                          <p className="text-xs text-[var(--text-tertiary)]">{inc.title}</p>
                        </div>
                        <Badge variant="destructive" className="text-xs">
                          {formatStatusLabel(inc.status)}
                        </Badge>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </QueryStateBoundary>
          </div>

          {/* Pending Walk-Ins card */}
          <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface-raised)] p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold">
                Pending Walk-Ins
                {(walkInsQuery.data ?? []).length > 0 && (
                  <Badge className="ml-2 text-xs">{(walkInsQuery.data ?? []).length}</Badge>
                )}
              </h2>
            </div>
            <QueryStateBoundary
              query={walkInsQuery}
              errorTitle="Couldn't load walk-ins"
              emptyMessage="No pending walk-ins"
              loadingFallback={<p className="text-sm text-[var(--text-tertiary)]">Loading…</p>}
            >
              {(walkIns) => (
                <ul className="space-y-2">
                  {walkIns.map((w) => (
                    <li key={w.id} className="text-sm">
                      <p className="font-medium">{w.visitorName}</p>
                      <p className="text-xs text-[var(--text-tertiary)]">
                        Unit {w.residentUnit} · {w.gateName}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </QueryStateBoundary>
          </div>

          {/* Gate Status card */}
          <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface-raised)] p-4">
            <h2 className="mb-3 text-sm font-semibold text-[var(--text-primary)]">Gate Status</h2>
            <QueryStateBoundary
              query={gatesQuery}
              errorTitle="Couldn't load gates"
              emptyMessage="No gates configured."
              loadingFallback={<p className="text-sm text-[var(--text-tertiary)]">Loading…</p>}
            >
              {(gates: PropertyGate[]) => {
                const devices: GuardDevice[] = devicesQuery.data ?? [];
                return (
                  <ul className="space-y-2">
                    {gates.map((gate) => {
                      const gateDevices = devices.filter((d) => d.gateId === gate.id);
                      const gateOnline =
                        gateDevices.length > 0 && gateDevices.some((d) => !d.isOffline);
                      return (
                        <li key={gate.id} className="flex items-center gap-2 text-sm">
                          {/* Supervisors can't read device sessions (PM-only), so
                              the online/offline indicator is omitted for them
                              rather than defaulting every gate to OFFLINE. */}
                          {isManager && (
                            <span
                              className="block rounded-full flex-shrink-0"
                              style={{
                                width: 8,
                                height: 8,
                                background: gateOnline
                                  ? 'var(--status-green)'
                                  : 'var(--status-red)',
                              }}
                            />
                          )}
                          <span className="text-[var(--text-primary)]">{gate.name}</span>
                          {isManager && (
                            <span
                              className="ml-auto text-xs font-medium"
                              style={{
                                color: gateOnline ? 'var(--status-green)' : 'var(--status-red)',
                              }}
                            >
                              {gateOnline ? 'ONLINE' : 'OFFLINE'}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                );
              }}
            </QueryStateBoundary>
          </div>

          {/* Upcoming Next 2 Hours card — driven by the invitation list, which
              is property-manager-only on the server, so hidden for supervisors. */}
          {isManager && (
            <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface-raised)] p-4">
              <h2 className="mb-3 text-sm font-semibold text-[var(--text-primary)]">
                Upcoming · Next 2 Hours
              </h2>
              <QueryStateBoundary
                query={upcomingQuery}
                errorTitle="Couldn't load upcoming visitors"
                emptyMessage="No visitors expected."
                loadingFallback={<p className="text-sm text-[var(--text-tertiary)]">Loading…</p>}
              >
                {(visitors: Invitation[]) => (
                  <ul className="space-y-2">
                    {visitors.map((inv) => (
                      <li key={inv.id} className="flex items-start gap-3 text-sm">
                        <span
                          className="text-xs font-semibold tabular-nums flex-shrink-0 mt-0.5"
                          style={{ color: 'var(--cta-primary-bg)' }}
                        >
                          {new Date(inv.validFrom).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                        <div>
                          <p className="font-medium text-[var(--text-primary)]">
                            {inv.visitorName}
                          </p>
                          <p className="text-xs text-[var(--text-tertiary)]">{inv.gateName}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </QueryStateBoundary>
            </div>
          )}

          {/* Module Status card — feature modules are configuration, which the
              server restricts to property managers (SI-07), so hidden for supervisors. */}
          {isManager && (
            <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface-raised)] p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-[var(--text-primary)]">Module Status</h2>
                <Link
                  to="/manager/modules"
                  className="text-xs text-[var(--text-tertiary)] flex items-center gap-1 hover:text-[var(--text-primary)]"
                >
                  Manage <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
              <QueryStateBoundary
                query={modulesQuery}
                errorTitle="Couldn't load modules"
                emptyMessage="No modules configured."
                loadingFallback={<p className="text-sm text-[var(--text-tertiary)]">Loading…</p>}
              >
                {(modules: PropertyModule[]) => (
                  <ul className="space-y-2">
                    {modules.map((mod) => (
                      <li key={mod.id} className="flex items-center justify-between text-sm">
                        <span className="text-[var(--text-primary)]">{mod.name}</span>
                        <Badge variant={mod.active ? 'default' : 'outline'}>
                          {mod.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </QueryStateBoundary>
            </div>
          )}
        </div>
      </div>

      {/* Event detail panel */}
      {selected && (
        <EventDetailPanel
          entry={selected}
          onClose={() => setSelected(null)}
          {...(pm
            ? {
                onFlag: handleFlag,
                onClearFlag: (e: PmAccessLogEntry) => clearFlagMutation.mutate(e.id),
                isFlagPending: flagMutation.isPending || clearFlagMutation.isPending,
              }
            : {})}
        />
      )}
    </div>
  );
}
